import axios, { AxiosResponse } from "axios";

export const STUDIO_PLUS_PAYMENTS_PROVIDER = "StudioPlusPayments";
export const STUDIO_PLUS_PAYMENT_COMPLETE_EVENT = "payment-complete";
export const STUDIO_PLUS_PAYMENT_METHODS_UPDATED_EVENT =
  "payment-methods-updated";

const TERMINAL_PAYMENT_STATUSES = ["succeeded", "failed", "canceled", "cancelled"];

export function isStudioPlusPaymentsProvider(authStore: any) {
  return (
    authStore?.onlinePaymentProvider === STUDIO_PLUS_PAYMENTS_PROVIDER &&
    !!authStore?.studioPlusPaymentsWebURL
  );
}

export function getStudioPlusPaymentsOrigin(webUrl?: string) {
  if (!webUrl) {
    return "";
  }

  try {
    return new URL(webUrl).origin;
  } catch {
    return "";
  }
}

export function buildStudioPlusPayUrl(
  webUrl: string,
  paymentSessionId: string
) {
  return `${trimBaseUrl(webUrl)}/Pay?paymentSessionId=${encodeURIComponent(
    paymentSessionId
  )}`;
}

export function buildStudioPlusPaymentMethodsUrl(
  webUrl: string,
  referenceKey: string
) {
  return `${trimBaseUrl(webUrl)}/PaymentMethods?referenceKey=${encodeURIComponent(
    referenceKey
  )}`;
}

export function isStudioPlusPaymentsMessage(
  event: MessageEvent,
  webUrl: string,
  messageType: string
) {
  const expectedOrigin = getStudioPlusPaymentsOrigin(webUrl);
  return (
    !!expectedOrigin &&
    event.origin === expectedOrigin &&
    typeof event.data === "object" &&
    event.data?.type === messageType
  );
}

export function isStudioPlusPaymentsTerminalStatus(status?: string) {
  return !!status && TERMINAL_PAYMENT_STATUSES.includes(status.toLowerCase());
}

export function isStudioPlusPaymentsSuccessStatus(status?: string) {
  return !!status && status.toLowerCase() === "succeeded";
}

export async function createStudioPlusPaymentSession(invoiceKey: string) {
  const response: AxiosResponse = await axios({
    method: "POST",
    url: "/ibyStudioPlusPayments/payment-session",
    data: {
      InvoiceKey: invoiceKey,
    },
  });

  return response.data;
}

export async function getStudioPlusPaymentStatus(paymentSessionId: string) {
  const response: AxiosResponse = await axios({
    method: "GET",
    url: `/ibyStudioPlusPayments/payment-status/${paymentSessionId}`,
  });

  return response.data;
}

export async function pollStudioPlusPaymentStatus(
  paymentSessionId: string,
  maxAttempts = 20,
  delayMs = 1500
) {
  let attempt = 0;

  while (attempt < maxAttempts) {
    const statusResponse = await getStudioPlusPaymentStatus(paymentSessionId);
    if (isStudioPlusPaymentsTerminalStatus(statusResponse?.Status)) {
      return statusResponse;
    }

    attempt += 1;
    await delay(delayMs);
  }

  return getStudioPlusPaymentStatus(paymentSessionId);
}

export async function createStudioPlusPaymentMethodsSession(clientKey?: string) {
  const response: AxiosResponse = await axios({
    method: "POST",
    url: "/ibyStudioPlusPayments/payment-methods-session",
    data: {
      ClientKey: clientKey,
    },
  });

  return response.data;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function trimBaseUrl(url: string) {
  return url.replace(/\/+$/, "");
}
